import { create } from "zustand";

export interface DocumentMetadata {
  node_id: string;
  field_id: string;
  field_name?: string;
  field_label?: string;
  [key: string]: unknown; // Allow for additional metadata properties
}

interface DocumentMetadataState {
  documents: DocumentMetadata[];
  setDocuments: (documents: DocumentMetadata[]) => void;
  addDocuments: (documents: DocumentMetadata[]) => void;
  clearDocuments: () => void;
  getDocumentsByFieldId: (fieldId: string) => DocumentMetadata[];
  getDocumentsByNodeId: (nodeId: string) => DocumentMetadata[];
  getDocumentsByFieldName: (fieldName: string) => DocumentMetadata[];
  getDocumentsByFieldAndNode: (
    fieldId?: string | null,
    nodeId?: string | null,
  ) => DocumentMetadata[];
}

export const useDocumentMetadataStore = create<DocumentMetadataState>(
  (set, get) => ({
    documents: [],

    setDocuments: (documents) => set({ documents }),

    addDocuments: (documents) =>
      set((state) => {
        // Merge with existing documents, avoiding duplicates based on node_id + field_id combination
        const existingKeys = new Set(
          state.documents.map((doc) => `${doc.node_id}-${doc.field_id}`),
        );
        const newDocuments = documents.filter(
          (doc) => !existingKeys.has(`${doc.node_id}-${doc.field_id}`),
        );
        return { documents: [...state.documents, ...newDocuments] };
      }),

    clearDocuments: () => set({ documents: [] }),

    getDocumentsByFieldId: (fieldId: string) => {
      const { documents } = get();
      return documents.filter(
        (doc) => doc.field_id === fieldId || doc.field_id === String(fieldId),
      );
    },

    getDocumentsByNodeId: (nodeId: string) => {
      const { documents } = get();
      return documents.filter(
        (doc) => doc.node_id === nodeId || doc.node_id === String(nodeId),
      );
    },

    getDocumentsByFieldName: (fieldName: string) => {
      const { documents } = get();
      return documents.filter(
        (doc) =>
          doc.field_name === fieldName || doc.field_name === String(fieldName),
      );
    },

    getDocumentsByFieldAndNode: (fieldId, nodeId) => {
      const { documents } = get();
      return documents.filter((doc) => {
        const matchesField =
          !fieldId ||
          doc.field_id === fieldId ||
          doc.field_id === String(fieldId);
        const matchesNode =
          !nodeId || doc.node_id === nodeId || doc.node_id === String(nodeId);
        return matchesField && matchesNode;
      });
    },
  }),
);
